import { useState } from "react";
import BackButton from "../../../../components/Button/BackButton";
import FormInput from "../../../../components/Form/FormInput";
import FormSelect from "../../../../components/Form/FormSelect";
import { btn } from "../../../../components/Button/buttonStyles";
import { AUTH_OPTIONS, DEFAULT_PORTS, ENCRYPTION_OPTIONS } from "./smtpOptions";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toForm = (smtp) => ({
  host: smtp?.host ?? "",
  port: String(smtp?.port ?? DEFAULT_PORTS.starttls),
  encryption: smtp?.encryption ?? "starttls",
  authentication: smtp?.authentication ?? "auto",
  username: smtp?.username ?? "",
  password: "",
  from_email: smtp?.from_email ?? "",
});

const SmtpServerForm = ({ smtp, onSave, onCancel }) => {
  const [form, setForm] = useState(toForm(smtp));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const passwordRequired = !smtp;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // Follow the encryption with its usual port unless a custom one was typed.
      if (name === "encryption" && Object.values(DEFAULT_PORTS).map(String).includes(prev.port)) {
        next.port = String(DEFAULT_PORTS[value]);
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const current = {};
    const port = Number(form.port);
    if (!form.host.trim()) current.host = "Host is required.";
    if (!form.port.trim()) current.port = "Port is required.";
    else if (!Number.isInteger(port) || port < 1 || port > 65535) {
      current.port = "Port must be a whole number between 1 and 65535.";
    }
    if (!form.username.trim()) current.username = "Username is required.";
    if (passwordRequired && !form.password) current.password = "Password is required.";
    if (!form.from_email.trim()) current.from_email = "From email is required.";
    else if (!EMAIL_RE.test(form.from_email.trim())) current.from_email = "Enter a valid email address.";
    return current;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const current = validate();
    setErrors(current);
    if (Object.keys(current).length) return;

    setSaving(true);
    const ok = await onSave({
      host: form.host.trim(),
      port: Number(form.port),
      encryption: form.encryption,
      authentication: form.authentication,
      username: form.username.trim(),
      ...(form.password ? { password: form.password } : {}),
      from_email: form.from_email.trim(),
    });
    setSaving(false);
    if (ok) onCancel();
  };

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <BackButton onClick={onCancel} />
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
          Edit SMTP Settings
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="space-y-5 rounded-lg border border-gray-100 bg-white p-5 shadow-sm"
      >
        <div className="grid grid-cols-1 items-start gap-x-5 gap-y-4 md:grid-cols-2">
          <FormInput
            label="Host"
            name="host"
            value={form.host}
            onChange={handleChange}
            placeholder="smtp.example.com"
            error={errors.host}
            required
          />
          <FormInput
            label="Port"
            name="port"
            type="number"
            value={form.port}
            onChange={handleChange}
            error={errors.port}
            required
          />
          <FormSelect
            label="Encryption"
            name="encryption"
            value={form.encryption}
            onChange={handleChange}
            options={ENCRYPTION_OPTIONS}
            required
          />
          <FormSelect
            label="Authentication"
            name="authentication"
            value={form.authentication}
            onChange={handleChange}
            options={AUTH_OPTIONS}
            required
          />
          <FormInput
            label="Username"
            name="username"
            value={form.username}
            onChange={handleChange}
            placeholder="info@example.com"
            error={errors.username}
            required
          />
          <FormInput
            label="Password"
            name="password"
            type="password"
            value={form.password}
            onChange={handleChange}
            placeholder={passwordRequired ? "" : "Leave blank to keep the current password"}
            error={errors.password}
            required={passwordRequired}
          />
          <FormInput
            label="From email"
            name="from_email"
            type="email"
            value={form.from_email}
            onChange={handleChange}
            placeholder="noreply@example.com"
            error={errors.from_email}
            required
          />
        </div>

        <div className="space-y-3 border-t border-gray-100 pt-3">
          <span className="block text-xs text-gray-500">
            Fields marked with <span className="text-danger-500">*</span> are mandatory.
          </span>
          <div className="flex gap-2 text-sm">
            <button type="submit" disabled={saving} className={btn("primary", "sm")}>
              {saving ? "Updating..." : "Update"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className={btn("secondary", "sm")}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SmtpServerForm;